<template>
  <div id="app">
    <!-- 导航栏 -->
    <nav
      class="bg-white shadow-md fixed w-full z-50 transition-all duration-300"
    >
      <div class="container mx-auto px-4">
        <div class="flex justify-between items-center py-4">
          <div class="flex items-center">
            <img src="@/assets/xgylLOGO.png" class="h-12" />
            <span class="ml-4 text-xl font-bold text-primary">{{
              $t("companyName")
            }}</span>
          </div>
          <!-- 桌面导航 -->
          <div class="hidden md:flex items-center space-x-8">
            <a
              href="#home"
              class="nav-link text-gray-900 text-base font-bold relative transition-colors duration-300 inline-block py-1"
              @click.prevent="scrollToSection('home')"
            >
              {{ $t("nav.home") }}
              <span
                class="nav-underline absolute bottom-0 left-0 w-0 h-2 bg-secondary transition-all duration-300"
              ></span>
            </a>
            <a
              href="#about"
              class="nav-link text-gray-900 text-base font-bold relative transition-colors duration-300 inline-block py-1 mx-8"
              @click.prevent="scrollToSection('about')"
            >
              {{ $t("nav.about") }}
              <span
                class="nav-underline absolute bottom-0 left-0 w-0 h-2 bg-secondary transition-all duration-300"
              ></span>
            </a>
            <a
              href="#solutions"
              class="nav-link text-gray-900 text-base font-bold relative transition-colors duration-300 inline-block py-1"
              @click.prevent="scrollToSection('solutions')"
            >
              {{ $t("nav.solutions") }}
              <span
                class="nav-underline absolute bottom-0 left-0 w-0 h-2 bg-secondary transition-all duration-300"
              ></span>
            </a>
            <a
              href="#contact"
              class="nav-link text-gray-900 text-base font-bold relative transition-colors duration-300 inline-block py-1 mx-8"
              @click.prevent="scrollToSection('contact')"
            >
              {{ $t("nav.contact") }}
              <span
                class="nav-underline absolute bottom-0 left-0 w-0 h-2 bg-secondary transition-all duration-300"
              ></span>
            </a>
            <!-- 语言切换按钮（默认蓝色，修复样式） -->
            <div class="ml-4 relative">
              <button
                class="!bg-[#0A2463] !text-white px-6 py-2 rounded-full transition duration-300 flex items-center hover:!bg-[#0A2463]/90"
                @click="toggleLangMenu"
              >
                <i class="fas fa-globe-asia mr-2"></i>
                <span>{{ currentLangText }}</span>
                <i class="fas fa-chevron-down ml-2 text-xs"></i>
              </button>
              <!-- 语言下拉菜单 -->
              <div
                v-if="langMenuOpen"
                class="absolute right-0 bg-white shadow-lg rounded-lg mt-2 py-2 z-20 w-36"
              >
                <div
                  class="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                  @click="changeLang('zh-tw')"
                >
                  繁體中文
                </div>
                <div
                  class="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                  @click="changeLang('en')"
                >
                  English
                </div>
              </div>
            </div>
          </div>

          <!-- 移动端菜单按钮 -->
          <div class="md:hidden">
            <button
              @click="toggleMobileMenu"
              class="text-gray-700 focus:outline-none"
            >
              <i class="fas fa-bars text-2xl"></i>
            </button>
          </div>
        </div>

        <!-- 移动端导航菜单 -->
        <div v-if="isMobileMenuOpen" id="mobile-menu" class="md:hidden pb-4">
          <a
            href="#home"
            class="block text-base py-2 text-gray-700 hover:text-primary transition-colors"
            @click.prevent="scrollToSection('home')"
            >{{ $t("nav.home") }}</a
          >
          <a
            href="#about"
            class="block text-base py-2 text-gray-700 hover:text-primary transition-colors"
            @click.prevent="scrollToSection('about')"
            >{{ $t("nav.about") }}</a
          >
          <a
            href="#solutions"
            class="block text-base py-2 text-gray-700 hover:text-primary transition-colors"
            @click.prevent="scrollToSection('solutions')"
            >{{ $t("nav.solutions") }}</a
          >
          <a
            href="#contact"
            class="block text-base py-2 text-gray-700 hover:text-primary transition-colors"
            @click.prevent="scrollToSection('contact')"
            >{{ $t("nav.contact") }}</a
          >

          <!-- 移动端语言切换（默认蓝色） -->
          <div class="mt-2">
            <button
              class="!bg-[#0A2463] !text-white px-4 py-2 rounded-full transition duration-300 flex items-center w-full justify-center hover:!bg-[#0A2463]/90"
              @click="toggleLangMenu"
            >
              <i class="fas fa-globe-asia mr-2"></i>
              <span>{{ currentLangText }}</span>
            </button>
            <div
              v-if="langMenuOpen"
              class="bg-white shadow-lg rounded-lg mt-2 py-2"
            >
              <div
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                @click="changeLang('zh-tw')"
              >
                繁體中文
              </div>
              <div
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                @click="changeLang('en')"
              >
                English
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 首屏英雄区 -->
    <section
      id="home"
      class="hero-section flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-900 to-indigo-800 pt-16"
    >
      <canvas id="hero-canvas"></canvas>
      <div class="container mx-auto px-4 z-10">
        <div class="hero-content text-center text-white">
          <h1
            class="text-5xl md:text-6xl font-bold mb-10 leading-tight text-white"
          >
            {{ $t("hero.title1") }}<br />
            <span class="text-secondary">{{ $t("hero.title2") }}</span>
          </h1>
          <p class="text-xl md:text-2xl mb-10 max-w-3xl mx-auto">
            {{ $t("hero.desc") }}
          </p>
          <div
            class="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
          >
            <a
              href="#solutions"
              class="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white/10 hover:text-white transition duration-300"
              @click.prevent="scrollToSection('solutions')"
            >
              {{ $t("hero.btn1") }}
            </a>
            <a
              href="#contact"
              class="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white/10 hover:text-white transition duration-300"
              @click.prevent="scrollToSection('contact')"
            >
              {{ $t("hero.btn2") }}
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- 关于我们 -->
    <section id="about" class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-primary mb-4">
            {{ $t("about.title") }}
          </h2>
          <div class="w-24 h-1 bg-secondary mx-auto"></div>
          <p class="mt-6 text-lg text-gray-600 max-w-3xl mx-auto">
            {{ $t("about.desc") }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div class="fade-in">
            <h3 class="text-2xl font-bold text-primary mb-6">
              {{ $t("about.profile.title") }}
            </h3>
            <p class="text-gray-700 mb-6 leading-relaxed">
              {{ $t("about.profile.desc1") }}
            </p>
            <p class="text-gray-700 mb-6 leading-relaxed">
              {{ $t("about.profile.desc2") }}
            </p>
            <p class="text-gray-700 mb-6 leading-relaxed">
              {{ $t("about.profile.desc3") }}
            </p>
            <div class="flex flex-wrap gap-6 mt-8">
              <div class="flex items-center">
                <div
                  class="w-16 h-16 bg-[#0A2463] rounded-full flex items-center justify-center !text-white text-xl font-bold flex-shrink-0"
                >
                  5+
                </div>
                <div class="ml-4">
                  <h4 class="text-lg font-bold text-[#0A2463]">
                    {{ $t("about.profile.exp") }}
                  </h4>
                </div>
              </div>
              <div class="flex items-center">
                <div
                  class="w-16 h-16 bg-[#0A2463] rounded-full flex items-center justify-center !text-white text-xl font-bold flex-shrink-0"
                >
                  200+
                </div>
                <div class="ml-4">
                  <h4 class="text-lg font-bold text-[#0A2463]">
                    {{ $t("about.profile.clients") }}
                  </h4>
                </div>
              </div>
              <div class="flex items-center">
                <div
                  class="w-16 h-16 bg-[#0A2463] rounded-full flex items-center justify-center !text-white text-xl font-bold flex-shrink-0"
                >
                  200+
                </div>
                <div class="ml-4">
                  <h4 class="text-lg font-bold text-[#0A2463]">
                    {{ $t("about.profile.experts") }}
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <div class="fade-in" style="animation-delay: 0.2s">
            <img
              src="@/assets/hk2.webp"
              class="rounded-lg shadow-xl w-full h-auto"
            />
          </div>
        </div>

        <!-- 核心优势 -->
        <div class="mt-24">
          <h3 class="text-4xl font-bold text-primary text-center mb-12">
            {{ $t("about.advantage.title") }}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              class="card bg-white p-8 rounded-xl shadow-lg fade-in hover:shadow-xl transition-shadow duration-300"
            >
              <!-- 安全图标 - 修复样式+类名 -->
              <div
                class="w-16 h-16 bg-[#0A2463] rounded-full flex items-center justify-center !text-white text-2xl mb-6 flex-shrink-0"
              >
                <i class="fa fa-shield-alt"></i>
              </div>
              <h4 class="text-xl font-bold text-[#0A2463] mb-4">
                {{ $t("about.advantage.safety") }}
              </h4>
              <p class="text-gray-700">
                {{ $t("about.advantage.safetyDesc") }}
              </p>
            </div>
            <div
              class="card bg-white p-8 rounded-xl shadow-lg fade-in hover:shadow-xl transition-shadow duration-300"
              style="animation-delay: 0.2s"
            >
              <!-- 创新图标 - 修复样式+类名 -->
              <div
                class="w-16 h-16 bg-[#0A2463] rounded-full flex items-center justify-center !text-white text-2xl mb-6 flex-shrink-0"
              >
                <i class="fa fa-brain"></i>
              </div>
              <h4 class="text-xl font-bold text-[#0A2463] mb-4">
                {{ $t("about.advantage.innovation") }}
              </h4>
              <p class="text-gray-700">
                {{ $t("about.advantage.innovationDesc") }}
              </p>
            </div>
            <div
              class="card bg-white p-8 rounded-xl shadow-lg fade-in hover:shadow-xl transition-shadow duration-300"
              style="animation-delay: 0.4s"
            >
              <!-- 服务图标 - 修复样式+类名 -->
              <div
                class="w-16 h-16 bg-[#0A2463] rounded-full flex items-center justify-center !text-white text-2xl mb-6 flex-shrink-0"
              >
                <i class="fa fa-handshake"></i>
              </div>
              <h4 class="text-xl font-bold text-[#0A2463] mb-4">
                {{ $t("about.advantage.service") }}
              </h4>
              <p class="text-gray-700">
                {{ $t("about.advantage.serviceDesc") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 解决方案 -->
    <section id="solutions" class="py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-primary mb-4">
            {{ $t("solutions.title") }}
          </h2>
          <div class="w-24 h-1 bg-secondary mx-auto"></div>
          <p class="mt-6 text-lg text-gray-600 max-w-3xl mx-auto">
            {{ $t("solutions.desc") }}
          </p>
        </div>
        <!-- 关键修改：grid列数改为 lg:grid-cols-4（大屏4列），保留md:grid-cols-2（平板2列） -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <!-- 数字支付解决方案（动画延迟：默认0s） -->
          <div
            class="solution-card bg-white rounded-xl shadow-lg overflow-hidden fade-in flex flex-col"
          >
            <img
              src="@/assets/1.png"
              alt="数字支付解决方案"
              class="w-full h-48 object-cover"
            />
            <!-- Card-body 用 Flex 填充剩余空间，底部按钮自动对齐 -->
            <div class="p-6 card-body flex-grow flex flex-col justify-between">
              <!-- 标题：固定最小高度 -->
              <h3 class="text-2xl font-bold text-primary mb-3 min-h-[50px]">
                {{ $t("solutions.payment.title") }}
              </h3>
              <!-- 描述：固定最小高度 -->
              <p class="text-gray-700 mb-6 min-h-[60px]">
                {{ $t("solutions.payment.desc") }}
              </p>
              <!-- 列表：固定最小高度（适配不同项数） -->
              <ul class="mb-6 space-y-2 min-h-[100px]">
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.payment.item1") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.payment.item2") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.payment.item3") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.payment.item4") }}</span>
                </li>
              </ul>
              <!-- 按钮：固定在底部 -->
              <a
                href="#"
                class="inline-block text-primary font-bold hover:text-primary/90 transition duration-300 flex items-center"
              >
                {{ $t("common.more") }} <i class="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          </div>

          <!-- 金融安全解决方案（动画延迟：0.2s） -->
          <div
            class="solution-card bg-white rounded-xl shadow-lg overflow-hidden fade-in flex flex-col"
            style="animation-delay: 0.2s"
          >
            <img
              src="@/assets/2.png"
              alt="金融安全解决方案"
              class="w-full h-48 object-cover"
            />
            <div class="p-6 card-body flex-grow flex flex-col justify-between">
              <h3 class="text-2xl font-bold text-primary mb-3 min-h-[50px]">
                {{ $t("solutions.security.title") }}
              </h3>
              <p class="text-gray-700 mb-6 min-h-[60px]">
                {{ $t("solutions.security.desc") }}
              </p>
              <ul class="mb-6 space-y-2 min-h-[100px]">
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.security.item1") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.security.item2") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.security.item3") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.security.item4") }}</span>
                </li>
              </ul>
              <a
                href="#"
                class="inline-block text-primary font-bold hover:text-primary/90 transition duration-300 flex items-center"
              >
                {{ $t("common.more") }} <i class="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          </div>

          <!-- 智能风控解决方案（动画延迟：0.4s） -->
          <div
            class="solution-card bg-white rounded-xl shadow-lg overflow-hidden fade-in flex flex-col"
            style="animation-delay: 0.4s"
          >
            <img
              src="@/assets/4.png"
              alt="智能风控解决方案"
              class="w-full h-48 object-cover"
            />
            <div class="p-6 card-body flex-grow flex flex-col justify-between">
              <h3 class="text-2xl font-bold text-primary mb-3 min-h-[50px]">
                {{ $t("solutions.risk.title") }}
              </h3>
              <p class="text-gray-700 mb-6 min-h-[60px]">
                {{ $t("solutions.risk.desc") }}
              </p>
              <ul class="mb-6 space-y-2 min-h-[100px]">
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.risk.item1") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.risk.item2") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.risk.item3") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.risk.item4") }}</span>
                </li>
              </ul>
              <a
                href="#"
                class="inline-block text-primary font-bold hover:text-primary/90 transition duration-300 flex items-center"
              >
                {{ $t("common.more") }} <i class="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          </div>

          <!-- 保险服务解决方案（动画延迟：0.6s） -->
          <div
            class="solution-card bg-white rounded-xl shadow-lg overflow-hidden fade-in flex flex-col"
            style="animation-delay: 0.6s"
          >
            <img
              src="@/assets/3.png"
              alt="保险服务解决方案"
              class="w-full h-48 object-cover"
            />
            <div class="p-6 card-body flex-grow flex flex-col justify-between">
              <h3 class="text-2xl font-bold text-primary mb-3 min-h-[50px]">
                {{ $t("solutions.insurance.title") }}
              </h3>
              <p class="text-gray-700 mb-6 min-h-[60px]">
                {{ $t("solutions.insurance.desc") }}
              </p>
              <ul class="mb-6 space-y-2 min-h-[100px]">
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.insurance.item1") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.insurance.item2") }}</span>
                </li>
                <li class="flex items-center">
                  <i class="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>{{ $t("solutions.insurance.item3") }}</span>
                </li>
              </ul>
              <a
                href="#"
                class="inline-block text-primary font-bold hover:text-primary/90 transition duration-300 flex items-center"
              >
                {{ $t("common.more") }} <i class="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们（优化：自适应平铺居中） -->
    <section id="contact" class="py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-primary mb-4">
            {{ $t("contact.title") }}
          </h2>
          <div class="w-24 h-1 bg-secondary mx-auto"></div>
          <p class="mt-6 text-lg text-gray-600 max-w-3xl mx-auto">
            {{ $t("contact.desc") }}
          </p>
        </div>

        <!-- 联系信息模块：自适应平铺居中 -->
        <div class="max-w-5xl mx-auto">
          <div class="fade-in">
            <div
              class="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 w-full"
            >
              <h3 class="text-2xl font-bold text-primary mb-8 text-center">
                {{ $t("contact.info.title") }}
              </h3>
              <div
                class="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-16 max-w-4xl mx-auto"
              >
                <div class="flex flex-col items-center text-center">
                  <div
                    class="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-4 flex-shrink-0"
                  >
                    <i class="fa fa-map-marker-alt text-2xl"></i>
                  </div>
                  <h4 class="text-lg font-bold text-gray-800 mb-2">
                    {{ $t("contact.info.address") }}
                  </h4>
                  <div class="text-left space-y-2">
                    <div class="flex items-center">
                      <i
                        class="fa fa-map-marker-alt mr-2"
                        style="color: #666"
                      ></i>
                      <p class="text-gray-700">
                        {{ $t("contact.info.addressDesc") }}
                      </p>
                    </div>
                    <div class="flex items-center">
                      <i
                        class="fa fa-map-marker-alt mr-2"
                        style="color: #666"
                      ></i>
                      <p class="text-gray-700">
                        {{ $t("contact.info.addressDesc1") }}
                      </p>
                    </div>
                    <div class="flex items-center">
                      <i
                        class="fa fa-map-marker-alt mr-2"
                        style="color: #666"
                      ></i>
                      <p class="text-gray-700">
                        {{ $t("contact.info.addressDesc2") }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="flex flex-col items-center text-center">
                  <div
                    class="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-4 flex-shrink-0"
                  >
                    <i class="fa fa-envelope text-2xl"></i>
                  </div>
                  <h4 class="text-lg font-bold text-gray-800 mb-2">
                    {{ $t("contact.info.email") }}
                  </h4>
                  <p class="text-gray-700">Service@yinlian-fintech.hk</p>
                </div>
              </div>

              <div class="mt-10 text-center">
                <h4 class="text-lg font-bold text-gray-800 mb-4">
                  {{ $t("contact.info.follow") }}
                </h4>
                <div class="flex justify-center space-x-6">
                  <!-- 微信图标（修复类名+强制样式） -->
                  <a
                    href="#"
                    class="w-12 h-12 bg-[#0A2463] rounded-full flex items-center justify-center !text-white hover:bg-[#0A2463]/90 transition duration-300"
                  >
                    <i class="fab fa-weixin text-xl"></i>
                  </a>
                  <!-- 领英图标 -->
                  <a
                    href="#"
                    class="w-12 h-12 bg-[#0A2463] rounded-full flex items-center justify-center !text-white hover:bg-[#0A2463]/90 transition duration-300"
                  >
                    <i class="fab fa-linkedin text-xl"></i>
                  </a>
                  <!-- 推特图标 -->
                  <a
                    href="#"
                    class="w-12 h-12 bg-[#0A2463] rounded-full flex items-center justify-center !text-white hover:bg-[#0A2463]/90 transition duration-300"
                  >
                    <i class="fab fa-twitter text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 页脚（优化：蓝色渐变背景 + 白色字体） -->
    <footer
      class="py-12 bg-gradient-to-b from-[#0A2463] to-[#0A2463] text-white"
    >
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <h4
              class="text-xl font-bold mb-4 border-b border-white/20 pb-3 text-white"
            >
              {{ $t("footer.about") }}
            </h4>
            <p class="text-white/80 text-sm mb-4 leading-relaxed">
              {{ $t("footer.aboutDesc") }}
            </p>
            <div class="flex space-x-4">
              <a
                href="#"
                class="text-white/80 hover:text-white transition duration-300"
              >
                <i class="fab fa-weixin text-xl"></i>
              </a>
              <a
                href="#"
                class="text-white/80 hover:text-white transition duration-300"
              >
                <i class="fab fa-linkedin text-xl"></i>
              </a>
              <a
                href="#"
                class="text-white/80 hover:text-white transition duration-300"
              >
                <i class="fab fa-twitter text-xl"></i>
              </a>
            </div>
          </div>
          <div>
            <h4
              class="text-xl font-bold mb-4 border-b border-white/20 pb-3 text-white"
            >
              {{ $t("footer.solutions") }}
            </h4>
            <ul class="space-y-2 text-white/80">
              <li>
                <a href="#" class="hover:text-white transition duration-300">{{
                  $t("solutions.payment.title")
                }}</a>
              </li>
              <li>
                <a href="#" class="hover:text-white transition duration-300">{{
                  $t("solutions.security.title")
                }}</a>
              </li>
              <li>
                <a href="#" class="hover:text-white transition duration-300">{{
                  $t("solutions.risk.title")
                }}</a>
              </li>
              <li>
                <a href="#" class="hover:text-white transition duration-300">{{
                  $t("solutions.insurance.title")
                }}</a>
              </li>
            </ul>
          </div>
          <div>
            <h4
              class="text-xl font-bold mb-4 border-b border-white/20 pb-3 text-white"
            >
              {{ $t("footer.links") }}
            </h4>
            <ul class="space-y-2 text-white/80">
              <li>
                <a
                  href="#home"
                  class="hover:text-white transition duration-300"
                  @click.prevent="scrollToSection('home')"
                  >{{ $t("nav.home") }}</a
                >
              </li>
              <li>
                <a
                  href="#about"
                  class="hover:text-white transition duration-300"
                  @click.prevent="scrollToSection('about')"
                  >{{ $t("nav.about") }}</a
                >
              </li>
              <li>
                <a
                  href="#solutions"
                  class="hover:text-white transition duration-300"
                  @click.prevent="scrollToSection('solutions')"
                  >{{ $t("nav.solutions") }}</a
                >
              </li>
              <li>
                <a
                  href="#contact"
                  class="hover:text-white transition duration-300"
                  @click.prevent="scrollToSection('contact')"
                  >{{ $t("nav.contact") }}</a
                >
              </li>
            </ul>
          </div>
          <div>
            <h4
              class="text-xl font-bold mb-4 border-b border-white/20 pb-3 text-white"
            >
              {{ $t("footer.contact") }}
            </h4>
            <ul class="space-y-3 text-white/80">
              <li class="flex items-start">
                <i class="fas fa-map-marker-alt mr-3 mt-1 text-white"></i>
                <span>{{ $t("contact.info.addressDesc") }}</span>
              </li>
              <li class="flex items-start">
                <i class="fas fa-map-marker-alt mr-3 mt-1 text-white"></i>
                <span>{{ $t("contact.info.addressDesc1") }}</span>
              </li>
              <li class="flex items-start">
                <i class="fas fa-map-marker-alt mr-3 mt-1 text-white"></i>
                <span>{{ $t("contact.info.addressDesc2") }}</span>
              </li>
              <li class="flex items-start">
                <i class="fas fa-envelope mr-3 mt-1 text-white"></i>
                <span>Service@yinlian-fintech.hk</span>
              </li>
            </ul>
          </div>
        </div>
        <div
          class="border-t border-white/20 pt-8 text-center text-white/70 text-sm"
        >
          <p>
            {{ $t("footer.copyright") }} © 2025 {{ $t("companyName") }}.
            {{ $t("footer.allRights") }}
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from "vue";
import * as THREE from "three";

// ===================== 动态引入外部资源 =====================
onMounted(() => {
  // 引入 Font Awesome
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css";
  document.head.appendChild(link);

  // 引入 Tailwind CSS
  const script = document.createElement("script");
  script.src = "https://cdn.tailwindcss.com";
  script.onload = () => {
    // 配置 Tailwind
    window.tailwind.config = {
      theme: {
        extend: {
          colors: {
            primary: "#0A2463",
            "primary-light": "#3b82f6",
            secondary: "#facc15",
          },
          fontFamily: {
            sans: ["Inter", "system-ui", "sans-serif"],
          },
        },
      },
    };
  };
  document.head.appendChild(script);
});

// ===================== 基础状态管理 =====================
const isMobileMenuOpen = ref(false); // 移动端菜单开关
const langMenuOpen = ref(false); // 语言菜单开关
const lang = ref("zh-tw"); // 默认语言：繁体中文

// ===================== 多语言核心配置 =====================
// 语言包（适配精简页面）
const messages = reactive({
  // 繁体中文
  "zh-tw": {
    companyName: "香港銀連金融科技",
    nav: {
      home: "首頁",
      about: "關於我們",
      solutions: "解決方案",
      contact: "聯繫我們",
    },
    hero: {
      title1: "創新金融科技",
      title2: "連接未來金融",
      desc: "香港銀連金融科技有限公司致力於為金融機構提供安全、高效、創新的金融科技解決方案",
      btn1: "探索解決方案",
      btn2: "聯繫我們",
    },
    about: {
      title: "關於我們",
      desc: "香港銀連金融科技有限公司是一家專注於金融科技領域的創新企業，致力於為金融機構提供全方位的科技解決方案",
      profile: {
        title: "公司簡介",
        desc1:
          "香港銀連金融科技有限公司成立於 2024 年，在中國上海、廣州及日本大阪設有分支機構，構建覆蓋境內外核心發達地區的綜合業務服務網絡。",
        desc2:
          "公司核心管理團隊來自國內頂尖銀行卡支付機構、四大商業銀行、知名證券公司及高校科研團隊，平均擁有三十年中國金融市場從業經驗與二十餘年支付及金融科技產業實戰經驗，對金融基礎設施建設、支付清算體系及金融創新具有深刻理解。",
        desc3:
          "銀連金融科技匯聚來自金融與科技領域的高端專業人才，具備成熟的金融科技研發、系統建設與落地實施能力，致力於以科技手段推動金融服務效率提升與業務模式創新。",
        exp: "年行業經驗",
        clients: "金融機構客戶",
        experts: "技術專家",
      },
      advantage: {
        title: "核心優勢",
        safety: "安全可靠",
        safetyDesc:
          "採⽤銀⾏級安全標準，多重加密技術保障數據安全，嚴格符閤國際⾦融監管要求，提供全流程安全穩定的⾦融科技服務。",
        innovation: "技術創新",
        innovationDesc:
          "持續⾼強度研髮投⼊，深度掌握⼈⼯智能、區塊鏈、⼤數據三⼤前沿技術，以技術迭代驅動産品 創新，賦能⾦融機構數字化轉型。",
        service: "專業服務",
        serviceDesc:
          "全⽣命週期項⽬服務體繫，從需求調研、⽅案定製，到繫統上線、運維保障全程跟進，確保客⼾業務⾼效落地、平穩運⾏。",
      },
    },
    solutions: {
      title: "解決方案",
      desc: "我們提供全方位的金融科技解決方案，助力金融機構數字化轉型，提升服務效率和客戶體驗",
      payment: {
        title: "數字支付解決方案",
        desc: "覆蓋移動⽀付、跨境⽀付、聚閤⽀付全場景，搭配完善的⽀付安全保障體繫，滿⾜個⼈與企業多樣化⽀付需求。",
        item1: "行動支付",
        item2: "跨境支付",
        item3: "聚合支付",
        item4: "支付安全保障",
      },
      security: {
        title: "金融安全解決方案",
        desc: "圍繞數據加密、⾝份認証、⻛險監控、閤規管理四⼤覈⼼，築牢⾦融數據與交易安全防線，防範各類⾦融⻛險與⽹絡攻擊。",
        item1: "數據加密",
        item2: "身份認證",
        item3: "風險監控",
        item4: "安全合規",
      },
      risk: {
        title: "智慧風控解決方案",
        desc: "依托AI與⼤數據技術搭建智能⻛控繫統，實現實時⻛險監控、精準詐騙檢測、科學信⽤評估、專業⻛險報告輸齣，提昇⻛險管理效率。",
        item1: "即時風險監控",
        item2: "詐騙檢測",
        item3: "信用評估",
        item4: "風險報告",
      },
      insurance: {
        title: "保險服務解決方案",
        desc: "提供保險運營與分銷平颱定製、可配置TPA理賠管理繫統開髮、保險計劃書與⽅案輸齣，助⼒保險機構數字化昇級。",
        item1: "可靈活定制的保險運營與分銷平台",
        item2: "可配置TPA理賠管理系統",
        item3: "計劃書與方案輸出",
      },
    },
    contact: {
      title: "聯繫我們",
      desc: "無論您有任何問題或合作意向，我們都將竭誠為您服務",
      info: {
        title: "聯繫資訊",
        address: "公司地址",
        addressDesc:
          "中國香港葵涌大連排道 162-170 號金龍工業中心二期 4樓 I-1室",
        addressDesc1: "中國上海市普陀區大渡河路 1438 號信泰中心",
        addressDesc2: "日本國大阪府大阪市北区黑崎田 3-26-1A",
        phone: "聯繫電話",
        email: "電子郵箱",
        follow: "關注我們",
      },
    },
    footer: {
      about: "關於我們",
      aboutDesc:
        "香港銀連金融科技致力於為金融機構提供創新的科技解決方案，推動金融行業數字化轉型。",
      solutions: "解決方案",
      links: "快速連結",
      contact: "聯繫我們",
      copyright: "版權所有",
      allRights: "保留所有權利",
    },
    common: {
      more: "了解更多",
    },
  },
  // 英文
  en: {
    companyName: "HONG KONG YINLIAN FINANCIAL TECHNOLOGY",
    nav: {
      home: "Home",
      about: "About Us",
      solutions: "Solutions",
      contact: "Contact Us",
    },
    hero: {
      title1: "Innovative Financial Technology",
      title2: "Connecting the Future of Finance",
      desc: "HONG KONG YINLIAN FINANCIAL TECHNOLOGY Co., Ltd. is committed to providing safe, efficient and innovative fintech solutions for financial institutions",
      btn1: "Explore Solutions",
      btn2: "Contact Us",
    },
    about: {
      title: "About Us",
      desc: "HONG KONG YINLIAN FINANCIAL TECHNOLOGY Co., Ltd. is an innovative enterprise focusing on the fintech field, dedicated to providing comprehensive technological solutions for financial institutions",
      profile: {
        title: "Company Profile",
        desc1:
          "Founded in 2024, Hong Kong Yinlian FinTech Co., Ltd. has branch offices in Shanghai, Guangzhou (China) and Osaka (Japan), building a comprehensive business service network covering core developed regions at home and abroad.",
        desc2:
          "The company's core management team comes from top domestic bank card payment institutions, the Big Four commercial banks, well-known securities companies and university research teams, with an average of 30 years of experience in China's financial market and more than 20 years of practical experience in the payment and fintech industry. They have a profound understanding of financial infrastructure construction, payment and settlement systems, and financial innovation.",
        desc3:
          "Yinlian FinTech brings together high-end professionals from the financial and technological fields, with mature capabilities in fintech R&D, system construction and implementation. We are committed to improving the efficiency of financial services and innovating business models through technological means.",
        exp: "Years of Industry Experience",
        clients: "Financial Institution Clients",
        experts: "Technical Experts",
      },
      advantage: {
        title: "Advantages",
        safety: "Safe and Reliable",
        safetyDesc:
          "Adopting bank-level security standards and multiple encryption technologies to ensure data security, we strictly comply with international financial regulatory requirements and provide full-process safe and stable fintech services.",
        innovation: "Technological Innovation",
        innovationDesc:
          "With continuous high-intensity R&D investment, we have a profound grasp of three cutting-edge technologies—artificial intelligence, blockchain, and big data. We drive product innovation through technological iteration and empower financial institutions in their digital transformation.",
        service: "Professional Services",
        serviceDesc:
          "Boasting a full-life-cycle project service system, we provide end-to-end follow-up from demand research and customized solutions to system launch and operation & maintenance support, ensuring the efficient implementation and stable operation of clients' businesses.",
      },
    },
    solutions: {
      title: "Solutions",
      desc: "We provide comprehensive fintech solutions to help financial institutions achieve digital transformation and improve service efficiency and customer experience",
      payment: {
        title: "Digital Payment Solutions",
        desc: "Covers all scenarios of mobile payment, cross-border payment and aggregated payment, paired with a comprehensive payment security guarantee system to meet the diverse payment needs of individuals and enterprises.",
        item1: "Mobile Payment",
        item2: "Cross-border Payment",
        item3: "Aggregated Payment",
        item4: "Payment Security Assurance",
      },
      security: {
        title: "Financial Security Solutions",
        desc: "Centering on four core areas—data encryption, identity authentication, risk monitoring and compliance management—we build a solid defense line for the security of financial data and transactions, preventing various financial risks and cyber attacks.",
        item1: "Data Encryption",
        item2: "Identity Authentication",
        item3: "Risk Monitoring",
        item4: "Security Compliance",
      },
      risk: {
        title: "Intelligent Risk Control Solutions",
        desc: "Leveraging AI and big data technologies to build an intelligent risk control system, we realize real-time risk monitoring, accurate fraud detection, scientific credit evaluation and professional risk report output, improving the efficiency of risk management.",
        item1: "Real-time Risk Monitoring",
        item2: "Fraud Detection",
        item3: "Credit Evaluation",
        item4: "Risk Reporting",
      },
      insurance: {
        title: "Insurance Service Solutions",
        desc: "We provide customized insurance operation and distribution platforms, development of configurable TPA claim management systems, and output of insurance proposals and solutions to empower insurance institutions in their digital upgrade.",
        item1:
          "Flexibly Customizable Insurance Operation and Distribution Platform",
        item2: "Configurable TPA Claim Management System",
        item3: "Proposal and Solution Output",
      },
    },
    contact: {
      title: "Contact Us",
      desc: "Whether you have any questions or cooperation intentions, we will serve you wholeheartedly",
      info: {
        title: "Contact Information",
        address: "Company Address",
        addressDesc:
          "Unit I-1, 4/F, Phase 2, Golden Dragon Industrial Centre, 162-170 Tai Lin Pai Road, Kwai Chung, Hong Kong, China",
        addressDesc1:
          "Xintai Center, 1438 Daduhe Road, Putuo District, Shanghai, China",
        addressDesc2:
          "3-26-1A Kurosakida, Kita-ku, Osaka City, Osaka Prefecture, Japan",
        phone: "Contact Phone",
        email: "Email",
        follow: "Follow Us",
      },
    },
    footer: {
      about: "About Us",
      aboutDesc:
        "HONG KONG YINLIAN FINANCIAL TECHNOLOGY is committed to providing innovative technological solutions for financial institutions and promoting the digital transformation of the financial industry.",
      solutions: "Solutions",
      links: "Quick Links",
      contact: "Contact Us",
      copyright: "Copyright",
      allRights: "All Rights Reserved",
    },
    common: {
      more: "Learn More",
    },
  },
});

// 多语言翻译函数
const $t = (path) => {
  const keys = path.split(".");
  let value = messages[lang.value];
  for (const key of keys) {
    if (value[key] === undefined) return path;
    value = value[key];
  }
  return value;
};

// 当前语言显示文本
const currentLangText = computed(() => {
  const langMap = {
    "zh-tw": "繁體中文",
    en: "English",
  };
  return langMap[lang.value];
});

// 切换语言
const changeLang = (newLang) => {
  lang.value = newLang;
  langMenuOpen.value = false;
  localStorage.setItem("preferred-lang", newLang);
};

// 切换菜单显示状态
const toggleLangMenu = () => {
  langMenuOpen.value = !langMenuOpen.value;
};
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// ===================== 锚点滚动动画（核心修复） =====================
const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (!section) return;

  // 关闭移动端菜单（如果打开）
  isMobileMenuOpen.value = false;

  // 平滑滚动到目标位置（预留导航栏高度）
  const navHeight = document.querySelector("nav").offsetHeight;
  const targetTop = section.offsetTop - navHeight;

  window.scrollTo({
    top: targetTop,
    behavior: "smooth", // 强制平滑滚动
  });
};

// ===================== 3D 星空背景效果 =====================
let scene, camera, renderer, starField;
const initStars = () => {
  const canvas = document.getElementById("hero-canvas");
  if (!canvas) return;

  // 初始化场景
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(
    75,
    canvas.clientWidth / canvas.clientHeight,
    0.1,
    1000,
  );
  camera.position.z = 30;

  // 渲染器配置
  renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true,
    antialias: true,
  });
  renderer.setSize(canvas.clientWidth, canvas.clientHeight);
  renderer.setPixelRatio(window.devicePixelRatio);

  // 创建星空
  const starGeometry = new THREE.BufferGeometry();
  const starMaterial = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 0.5,
    sizeAttenuation: true,
  });

  const starVertices = [];
  for (let i = 0; i < 2000; i++) {
    const x = (Math.random() - 0.5) * 2000;
    const y = (Math.random() - 0.5) * 2000;
    const z = (Math.random() - 0.5) * 2000;
    starVertices.push(x, y, z);
  }
  starGeometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(starVertices, 3),
  );
  starField = new THREE.Points(starGeometry, starMaterial);
  scene.add(starField);

  // 动画循环
  const animate = () => {
    requestAnimationFrame(animate);
    starField.rotation.x += 0.0005;
    starField.rotation.y += 0.0005;
    renderer.render(scene, camera);
  };
  animate();

  // 窗口适配
  window.addEventListener("resize", () => {
    camera.aspect = canvas.clientWidth / canvas.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);
  });
};

// ===================== 导航下划线交互 =====================
const initNavUnderline = () => {
  const navLinks = document.querySelectorAll(".nav-link");
  navLinks.forEach((link) => {
    link.addEventListener("mouseenter", () => {
      const underline = link.querySelector(".nav-underline");
      if (underline) underline.style.width = "100%";
    });
    link.addEventListener("mouseleave", () => {
      const underline = link.querySelector(".nav-underline");
      if (underline) underline.style.width = "0";
    });
  });
};

// ===================== 生命周期钩子 =====================
onMounted(() => {
  // 初始化3D背景
  initStars();

  // 初始化导航下划线
  initNavUnderline();

  // 读取本地语言偏好
  const savedLang = localStorage.getItem("preferred-lang");
  if (savedLang && ["zh-tw", "en"].includes(savedLang)) {
    lang.value = savedLang;
  }

  // 导航栏滚动效果
  const nav = document.querySelector("nav");
  window.addEventListener("scroll", () => {
    if (window.scrollY > 50) {
      nav.classList.add("bg-white/95", "backdrop-blur-md", "shadow-lg");
      nav.classList.remove("bg-white");
    } else {
      nav.classList.remove("bg-white/95", "backdrop-blur-md", "shadow-lg");
      nav.classList.add("bg-white");
    }
  });

  // 淡入动画触发（滚动监听）
  const fadeElements = document.querySelectorAll(".fade-in");
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = "running";
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1 },
  );

  fadeElements.forEach((el) => {
    el.style.animationPlayState = "paused";
    observer.observe(el);
  });
});

onUnmounted(() => {
  // 销毁3D资源
  if (renderer) renderer.dispose();
  if (starField) {
    starField.geometry.dispose();
    starField.material.dispose();
  }
});

// 暴露模板使用的方法
defineExpose({
  $t,
  currentLangText,
  langMenuOpen,
  isMobileMenuOpen,
  toggleLangMenu,
  toggleMobileMenu,
  changeLang,
  scrollToSection,
});
</script>

<style scoped lang="less">
// 全局样式
:root {
  --primary: #0a2463; // 主色：深蓝色
  --primary-light: #3b82f6; // 主色浅版
  --secondary: #facc15; // 辅助色：黄色
  --secondary-light: #fde047; // 辅助色浅版
}

// 补充Tailwind未覆盖的颜色变量
.text-primary {
  color: var(--primary) !important;
}

.bg-primary {
  background-color: var(--primary) !important;
}

.hover\:bg-primary:hover {
  background-color: var(--primary) !important;
}

.hover\:bg-primary\/90:hover {
  background-color: rgba(30, 64, 175, 0.9) !important;
}

.hover\:text-primary:hover {
  color: var(--primary) !important;
}

.hover\:text-primary\/90:hover {
  color: rgba(30, 64, 175, 0.9) !important;
}

.text-secondary {
  color: var(--secondary) !important;
}

.bg-secondary {
  background-color: var(--secondary) !important;
}

.hover\:bg-secondary:hover {
  background-color: var(--secondary) !important;
}

// 基础样式
.hero-section {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
}

#hero-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.hero-content {
  position: relative;
  z-index: 10;
}

// 导航栏样式
nav {
  transition: all 0.3s ease;
  z-index: 50;
}

nav.bg-white {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.nav-link {
  // 强制默认黑色字体
  color: #000 !important;

  // hover时文字变为指定蓝色 #0A2463
  &:hover {
    color: #0a2463 !important;

    .nav-underline {
      width: 100%;
      transform-origin: left; // 下划线从左到右划出
    }
  }

  // 鼠标移出时下划线从右到左缩回
  &:not(:hover) .nav-underline {
    width: 0;
    transform-origin: right;
  }
}

.nav-underline {
  // 黄色下划线（#facc15），厚度2px
  background-color: #facc15 !important;
  height: 2px;
}

// 语言切换按钮强制默认蓝色（优先级最高）
.lang-btn {
  background-color: var(--primary) !important;
  color: white !important;
}

// 卡片样式
.solution-card {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
}

.card {
  transition: all 0.3s ease;
}

// 淡入动画
.fade-in {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeIn 0.8s ease forwards;
  animation-play-state: paused;
}

@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 响应式调整
@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 3rem !important;
  }

  :deep(.grid-cols-4) {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  // 移动端联系信息适配
  #contact .grid-cols-3 {
    grid-template-columns: 1fr !important;
    gap: 4rem !important;
  }
}

@media (max-width: 480px) {
  .hero-content h1 {
    font-size: 2.5rem !important;
  }

  :deep(.grid-cols-4) {
    grid-template-columns: 1fr !important;
  }

  footer .grid-cols-4 {
    grid-template-columns: 1fr !important;
    gap: 4rem !important;
  }
}
</style>
